import type {
  ResourceHubArticleCard,
  ResourceHubLocale,
  ResourceHubStaticCopy,
  ResourceHubUtilityCard,
} from '../../types/resource-hub';
import type { ResourceArticleContentId } from '../../types/resource-article-published';
import { trilingualEmbedHubHref } from '../../pages/resources/shared/trilingual-embed-hreflang';
import { TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID } from '../../pages/resources/shared/trilingual-embed-articles-seo';
import { getResourceArticlePublishedIsoDay } from './resource-article-published-dates';
import { parseResourceIsoDayToUtcNoon } from './format-resource-article-date';
import { HIGHEST_SALARIES_FINLAND_URL_PATHS } from '../resource-pages/highest-salaries-finland/highest-salaries-finland-urls';
import { COOL_FINNISH_TECH_URL_PATHS } from '../resource-pages/cool-finnish-tech-companies/cool-finnish-tech-urls';
import {
  getJobSearchGuideAbsolutePaths,
  getJuniorsHubAbsolutePath,
} from '../seo/indexation-discovery-links';

/** Shared layout copy for the three resource hub landing pages. */
export const RESOURCE_HUB_STATIC_COPY: ResourceHubStaticCopy = {
  breadcrumbHubLabel: {
    en: 'Resources',
    fi: 'Työelämä',
    sv: 'Arbetsliv',
  },
  h1: {
    en: 'Finland Job Market Guides & Career Tips',
    fi: 'Suomen työmarkkinaoppaat ja uravinkit',
    sv: 'Guider till jobb i Finland och karriärtips',
  },
  intro: {
    en: 'Guides to salaries, hiring trends, and job searching in Finland, built from live listings data.',
    fi: 'Oppaita palkoista, rekrytointitrendeistä ja työnhausta Suomessa, koottu ajantasaisesta ilmoitusdatasta.',
    sv: 'Guider om löner, rekryteringstrender och jobbsökning i Finland, byggda på aktuell annonsdata.',
  },
  utilitySectionTitle: {
    en: 'Tools & directories',
    fi: 'Hakutyökalut ja hakemistot',
    sv: 'Verktyg och kataloger',
  },
  utilitySectionSubtitle: {
    en: 'Search shortcuts, rankings, and city pages, not long-form guides.',
    fi: 'Pikahaut, sijoitukset ja kaupunkisivut, eivät pitkiä oppaita.',
    sv: 'Sökgenvägar, rankningar och stadssidor, inte långa guider.',
  },
  readMore: {
    en: 'Read more →',
    fi: 'Lue lisää →',
    sv: 'Läs mer →',
  },
  posted: {
    en: 'Posted',
    fi: 'Julkaistu',
    sv: 'Publicerad',
  },
};

function enOnly(path: string): Record<ResourceHubLocale, string> {
  const p = path as `/${string}`;
  return { en: p, fi: p, sv: p };
}

function pub(id: ResourceArticleContentId): string {
  return getResourceArticlePublishedIsoDay(id);
}

/**
 * Article grid: same cards on `/en/resources`, `/fi/tyoelama`, `/sv/arbetsliv`.
 * Where only an English article exists, FI/SV hubs link to the English URL.
 * Trilingual HTML-embed articles use locale-specific paths under /fi/tyoelama and /sv/arbetsliv.
 */
export const RESOURCE_HUB_ARTICLE_CARDS: ResourceHubArticleCard[] = [
  {
    id: 'best-job-websites',
    datePublished: pub('best-job-websites'),
    imageSrc: '/resources/job-websites-finland.webp',
    copy: {
      en: {
        title: 'Best Job Websites in Finland',
        description: 'Discover the best job search websites in Finland for finding your next job.',
        imageAlt: 'Best Job Websites in Finland',
      },
      fi: {
        title: 'Parhaat työnhakusivustot Suomessa',
        description:
          'Vertaa suuria työportaaleja ja opi, mitä kautta työpaikat Suomessa löytyvät parhaiten.',
        imageAlt: 'Parhaat työnhakusivustot Suomessa',
      },
      sv: {
        title: 'Bästa jobbsajter i Finland',
        description:
          'Jämför stora jobbportaler och hur du hittar nästa jobb i Finland.',
        imageAlt: 'Bästa jobbsajter i Finland',
      },
    },
    href: {
      en: '/en/resources/jobs-in-finland/best-job-websites-finland',
      fi: '/fi/tyoelama/tyot-suomessa/parhaat-tyopaikkasivustot-suomessa',
      sv: '/sv/arbetsliv/jobb-i-finland/basta-jobbsajter-finland',
    },
  },
  {
    id: 'cool-tech-english',
    datePublished: pub('cool-tech-english'),
    imageSrc:
      'https://images.unsplash.com/photo-1522885147691-06d859633fb8?q=80&w=800&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    copy: {
      en: {
        title: 'Cool Finnish Tech Companies Working in English',
        description:
          'Discover the coolest tech companies in Finland that embrace English as their working language.',
        imageAlt: 'Cool Finnish Tech Companies',
      },
      fi: {
        title: 'Siistit suomalaiset teknologiayritykset (englanti työkielenä)',
        description:
          'Tutustu suomalaisiin tech-yrityksiin, jotka työskentelevät englanniksi ja rekrytoivat kansainvälisesti.',
        imageAlt: 'Suomalaiset teknologiayritykset',
      },
      sv: {
        title: 'Häftiga finska techbolag som jobbar på engelska',
        description:
          'Upptäck techföretag i Finland som använder engelska som arbetsspråk.',
        imageAlt: 'Finska techbolag',
      },
    },
    href: {
      en: COOL_FINNISH_TECH_URL_PATHS.en,
      fi: COOL_FINNISH_TECH_URL_PATHS.fi,
      sv: COOL_FINNISH_TECH_URL_PATHS.sv,
    },
  },
  {
    id: 'job-market-helsinki-2026',
    datePublished: pub('job-market-helsinki-2026'),
    imageSrc: '/resources/helsinki-job-market.webp',
    copy: {
      en: {
        title: 'Helsinki Job Market 2026: Salaries, Remote Work & In-Demand Skills',
        description:
          "Salary trends, remote and hybrid roles, and the most in-demand skills in Finland's capital.",
        imageAlt: 'Job Market in Helsinki 2026',
      },
      fi: {
        title: 'Helsingin työmarkkina 2026: palkat, etätyö ja kysytyt taidot',
        description:
          'Palkkatrendit, etä- ja hybridityö sekä kysytyimmät osaamisalueet pääkaupunkiseudulla.',
        imageAlt: 'Työmarkkinat Helsinki 2026',
      },
      sv: {
        title: 'Helsingfors arbetsmarknad 2026: löner, distansarbete och efterfrågade kompetenser',
        description:
          'Lönetrender, distans- och hybridjobb samt mest efterfrågade färdigheter i huvudstadsregionen.',
        imageAlt: 'Arbetsmarknad Helsingfors 2026',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['job-market-helsinki-2026']),
  },
  {
    id: 'job-market-tampere-2026',
    datePublished: pub('job-market-tampere-2026'),
    imageSrc: '/resources/tampere-job-market.webp',
    copy: {
      en: {
        title: 'Job Market in Tampere (2026): Salaries, Flexibility & Skills',
        description:
          "Salary trends, flexible work, and in-demand skills in Finland's second-largest urban region.",
        imageAlt: 'Job Market in Tampere 2026',
      },
      fi: {
        title: 'Työmarkkinat Tampereella (2026): palkat, joustavuus ja taidot',
        description:
          'Palkkatrendit, joustavat työtavat ja kysytyimmät taidot Suomen toiseksi suurimmalla kaupunkiseudulla.',
        imageAlt: 'Työmarkkinat Tampere 2026',
      },
      sv: {
        title: 'Arbetsmarknaden i Tammerfors (2026): löner, flexibilitet och kompetens',
        description:
          'Lönetrender, flexibla arbetssätt och mest efterfrågade färdigheter i Finlands näst största region.',
        imageAlt: 'Arbetsmarknad Tammerfors 2026',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['job-market-tampere-2026']),
  },
  {
    id: 'highest-salaries-finland',
    datePublished: pub('highest-salaries-finland'),
    imageSrc: 'https://images.unsplash.com/photo-1538332576228-eb5b4c4de6f5?w=800&q=80&auto=format&fit=crop',
    copy: {
      en: {
        title: 'Where Are the Highest Salaries in Finland?',
        description:
          'Compare salaries across Helsinki, Espoo, Tampere, Turku, and Oulu, with cost-of-living context and real purchasing power.',
        imageAlt: 'Highest salaries in Finland, regional comparison',
      },
      fi: {
        title: 'Missä Suomessa on korkeimmat palkat?',
        description:
          'Vertaa palkkoja Helsingissä, Espoossa, Tampereella, Turussa ja Oulussa, elinkustannukset ja ostovoima mukana.',
        imageAlt: 'Korkeimmat palkat Suomessa, aluevertailu',
      },
      sv: {
        title: 'Var är de högsta lönerna i Finland?',
        description:
          'Jämför löner i Helsingfors, Esbo, Tammerfors, Åbo och Uleåborg, med levnadskostnader och köpkraft.',
        imageAlt: 'Högsta lönerna i Finland, regional jämförelse',
      },
    },
    href: {
      en: HIGHEST_SALARIES_FINLAND_URL_PATHS.en,
      fi: HIGHEST_SALARIES_FINLAND_URL_PATHS.fi,
      sv: HIGHEST_SALARIES_FINLAND_URL_PATHS.sv,
    },
  },
  {
    id: 'technology-salary-guide-202603',
    datePublished: pub('technology-salary-guide-202603'),
    imageSrc: '/resources/salary-guide.webp',
    copy: {
      en: {
        title: 'Technology Salary Guide: Finland, March 2026',
        description:
          'Median salaries for tech roles: Manager, Senior, Mid-Level, Junior, and Intern. Percentile ranges and sample sizes, updated March 2026.',
        imageAlt: 'Technology salary guide Finland, March 2026',
      },
      fi: {
        title: 'Teknologia-alan palkkaopas: Suomi, maaliskuu 2026',
        description:
          'Mediaanipalkat rooleittain: esihenkilö, seniori, keskitaso, juniori ja harjoittelija. Prosenttipisteet ja otoskoot.',
        imageAlt: 'Teknologia-alan palkkaopas Suomi 2026',
      },
      sv: {
        title: 'Tekniklöneguide: Finland, mars 2026',
        description:
          'Medianlöner per roll: chef, senior, mellannivå, junior och praktikant. Percentiler och urvalsstorlekar.',
        imageAlt: 'Tekniklöneguide Finland 2026',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['technology-salary-guide-202603']),
  },
  {
    id: 'technology-salary-guide-202604',
    datePublished: pub('technology-salary-guide-202604'),
    imageSrc: '/resources/tech-salaries-in-finland.webp',
    copy: {
      en: {
        title: 'Technology Salary Guide: Finland, April 2026',
        description:
          'Monthly salaries by seniority, top roles from trades to software engineers, and onsite vs hybrid vs remote, based on 1,831 listings.',
        imageAlt: 'Technology salary guide Finland April 2026',
      },
      fi: {
        title: 'Teknologia-alan palkkaopas: Suomi, huhtikuu 2026',
        description:
          'Kuukausipalkat tason mukaan, yleisimmät roolit ammateista kehittäjiin sekä lähityö, hybridit ja etätyö, yhteensä 1 831 ilmoitusta.',
        imageAlt: 'Teknologia-alan palkkaopas Suomi huhtikuu 2026',
      },
      sv: {
        title: 'Teknologi Löneguide: Finland, april 2026',
        description:
          'Månadslöner per senioritet, vanligaste rollerna från yrken till mjukvara samt på plats, hybrid och distans, totalt 1 831 annonser.',
        imageAlt: 'Teknologilöneguide Finland april 2026',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['technology-salary-guide-202604']),
  },
  {
    id: 'hidden-job-market-networking-202603',
    datePublished: pub('hidden-job-market-networking-202603'),
    imageSrc: '/resources/who-you-know-matters-in-finland.webp',
    copy: {
      en: {
        title: "Finland's Hidden Job Market & Networking (March 2026)",
        description:
          '21% of Finnish jobs never reach Työmarkkinatori. Private-channel share by field, salary non-disclosure, and why networking matters.',
        imageAlt: 'Hidden job market and networking in Finland',
      },
      fi: {
        title: 'Piilotyömarkkinat ja verkostoituminen (maaliskuu 2026)',
        description:
          '21 % suomalaisista työpaikoista ei koskaan näy Työmarkkinatorilla. Yksityiskanavien osuus aloittain, palkkatietojen piilottaminen ja miksi verkostoituminen ratkaisee.',
        imageAlt: 'Piilotyömarkkinat Suomessa',
      },
      sv: {
        title: 'Dolda arbetsmarknaden och nätverkande (mars 2026)',
        description:
          '21 % av finska jobb når aldrig Työmarkkinatori. Andel privata kanaler per bransch, lönehemlighållande och varför nätverkande spelar roll.',
        imageAlt: 'Dold arbetsmarknad i Finland',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['hidden-job-market-networking-202603']),
  },
  {
    id: 'finland-job-market-march-2026',
    datePublished: pub('finland-job-market-march-2026'),
    imageSrc: '/resources/safar-safarov-koOdUvfGr4c-unsplash.webp',
    imageLoading: 'lazy',
    copy: {
      en: {
        title: 'Finland Job Market: March 2026',
        description:
          "Healthcare leads Finland's 16,700+ active listings in March 2026. Full breakdown by field, salary by seniority, and remote work rates.",
        imageAlt: 'Finland job market, hiring fields and salary data',
      },
      fi: {
        title: 'Suomen työmarkkinat: maaliskuu 2026',
        description:
          'Terveydenhuolto johtaa Suomen yli 16 700 aktiivista työpaikkailmoitusta maaliskuussa 2026. Täysi erittely aloittain, palkat kokemustason mukaan ja etätyöosuudet.',
        imageAlt: 'Suomen työmarkkinat, rekrytointi ja palkkatiedot',
      },
      sv: {
        title: 'Finlands arbetsmarknad: mars 2026',
        description:
          'Vården leder Finlands 16 700+ aktiva annonser i mars 2026. Full uppdelning per bransch, medianlön per senioritet och distansarbets­frekvenser.',
        imageAlt: 'Finlands arbetsmarknad, rekrytering och lönedata',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['finland-job-market-march-2026']),
  },
  {
    id: 'remote-work-finland',
    datePublished: pub('remote-work-finland'),
    imageSrc: '/resources/remote-work-in-finland.webp',
    imageLoading: 'lazy',
    copy: {
      en: {
        title: 'Remote Work in Finland (2026)',
        description:
          'How common is fully remote work? Field and seniority splits from 16,700+ Finnish listings: hybrid, onsite, and what to expect in your search.',
        imageAlt: 'Remote work in Finland, job market statistics',
      },
      fi: {
        title: 'Etätyö Suomessa (2026)',
        description:
          'Kuinka yleistä täysin etänä tehtävä työ on? Alat ja kokemustaso yli 16 700 suomalaisen ilmoituksen pohjalta: hybridit, lähityö ja mitä odottaa.',
        imageAlt: 'Etätyö Suomessa, tilastot',
      },
      sv: {
        title: 'Distansarbete i Finland (2026)',
        description:
          'Hur vanligt är helt distansarbete? Fält och erfarenhetsnivå utifrån 16 700+ finska annonser: hybrid, på plats och vad du kan förvänta dig.',
        imageAlt: 'Distansarbete i Finland, statistik',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['remote-work-finland']),
  },
  {
    id: 'salary-transparency',
    datePublished: pub('salary-transparency'),
    imageSrc: '/resources/salary-transparency-in-finland.webp',
    imageLoading: 'lazy',
    copy: {
      en: {
        title: 'Salary Transparency in Finnish Job Listings',
        description:
          'Only about one in four ads include pay. Which industries disclose salaries, how junior vs senior compares, and what changes with the EU directive.',
        imageAlt: 'Salary transparency in Finnish job listings',
      },
      fi: {
        title: 'Palkkaläpinäkyvyys suomalaisissa työpaikkailmoituksissa',
        description:
          'Vain noin neljännes ilmoituksista näyttää palkan. Mitkä alat kertovat palkasta, miten juniorit ja seniorit eroavat ja mitä EU-direktiivi muuttaa.',
        imageAlt: 'Palkkaläpinäkyvyys Suomessa',
      },
      sv: {
        title: 'Lönetransparens i finska jobbannonser',
        description:
          'Bara omkring en av fyra annonser visar lön. Vilka branscher redovisar löner, skillnader junior vs senior och vad EU-direktivet kan förändra.',
        imageAlt: 'Lönetransparens i Finland',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['salary-transparency']),
  },
  {
    id: 'salary-transparency-law-2026',
    datePublished: pub('salary-transparency-law-2026'),
    imageSrc: '/resources/salary-transparency-in-finland.webp',
    imageLoading: 'lazy',
    copy: {
      en: {
        title: "Finland's Pay Transparency Law 2026",
        description:
          'From 18 May 2026, employers must share salary ranges before negotiations. Today most listings still hide pay, so see what changes for you.',
        imageAlt: 'Finland pay transparency law 2026, job seeker guide',
      },
      fi: {
        title: 'Palkkaläpinäkyvyyslaki Suomessa 2026',
        description:
          '18.5.2026 alkaen työnantajien on jaettava palkkaväli rekrytoinnissa. Katso nykyiset ilmoitusasteet ja vaikutukset työnhakijoille.',
        imageAlt: 'Palkkaläpinäkyvyyslaki Suomessa 2026',
      },
      sv: {
        title: 'Lönetransparenslag i Finland 2026',
        description:
          'Från 18 maj 2026 måste arbetsgivare dela löneintervall i rekrytering. Se dagens transparensnivåer och vad de betyder för kandidater.',
        imageAlt: 'Lönetransparenslag i Finland 2026',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['salary-transparency-law-2026']),
  },
  {
    id: 'linkedin-finland',
    datePublished: pub('linkedin-finland'),
    imageSrc: '/resources/how-to-use-linkedin.webp',
    imageLoading: 'lazy',
    copy: {
      en: {
        title: 'How to Use LinkedIn to Find a Job in Finland',
        description:
          'Profile, search, Easy Apply, and networking the Finnish way, plus jobs that never show up on LinkedIn alone.',
        imageAlt: 'Using LinkedIn to find a job in Finland',
      },
      fi: {
        title: 'LinkedIn työnhaussa Suomessa',
        description:
          'Profiili, haku, Easy Apply ja verkostoituminen suomalaisittain, sekä työpaikat, joita ei löydy vain LinkedInistä.',
        imageAlt: 'LinkedIn ja työnhaku Suomessa',
      },
      sv: {
        title: 'Så använder du LinkedIn för att hitta jobb i Finland',
        description:
          'Profil, sökning, Easy Apply och nätverkande på finskt vis, plus jobb som inte syns bara på LinkedIn.',
        imageAlt: 'LinkedIn och jobbsökning i Finland',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['linkedin-finland']),
  },
  {
    id: 'cv-finnish-employer',
    datePublished: pub('cv-finnish-employer'),
    imageSrc: '/resources/how-to-write-cv.webp',
    imageLoading: 'lazy',
    copy: {
      en: {
        title: 'How to Write a CV for a Finnish Employer',
        description:
          'Length, no photo, languages, and relocation: what Finnish recruiters scan for first, with current market context from live listings.',
        imageAlt: 'Writing a CV for a Finnish employer',
      },
      fi: {
        title: 'CV suomalaiselle työnantajalle',
        description:
          'Pituus, ei kuvaa, kielet ja muutto: mitä rekrytoijat katsovat ensin, live-ilmoitusten markkinakontekstilla.',
        imageAlt: 'CV Suomeen',
      },
      sv: {
        title: 'Hur du skriver ett CV för en finsk arbetsgivare',
        description:
          'Längd, inget foto, språk och flytt: vad rekryterare tittar på först, med marknadskontext från riktiga annonser.',
        imageAlt: 'CV för Finland',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['cv-finnish-employer']),
  },
  {
    id: 'cv-template-finland',
    datePublished: pub('cv-template-finland'),
    imageSrc: '/resources/cv-template-finland.webp',
    imageLoading: 'lazy',
    copy: {
      en: {
        title: 'Finnish CV template (free download)',
        description:
          'Three free templates built for the Finnish job market: the section order employers expect, Finnish date format, and a layout recruitment software can read.',
        imageAlt: 'Finnish CV template, free download',
      },
      fi: {
        title: 'CV-pohja: ilmainen ansioluettelopohja',
        description:
          'Kolme ilmaista pohjaa suomalaiseen työnhakuun: oikea osiojärjestys, suomalainen päivämäärämuoto ja IT-taidot omana osionaan.',
        imageAlt: 'Ilmainen CV-pohja ladattavaksi',
      },
      sv: {
        title: 'CV-mall (gratis nedladdning)',
        description:
          'Tre gratis mallar för finsk jobbsökning: rätt sektionsordning, finskt datumformat och en layout som rekryteringssystem kan läsa.',
        imageAlt: 'Gratis CV-mall att ladda ner',
      },
    },
    href: trilingualEmbedHubHref(TRILINGUAL_EMBED_ARTICLES_SEO_BY_HUB_ID['cv-template-finland']),
  },
];

export const RESOURCE_HUB_UTILITY_CARDS: ResourceHubUtilityCard[] = [
  {
    id: 'job-search-finland',
    imageSrc: '/resources/job-websites-finland.webp',
    imageLoading: 'lazy',
    copy: {
      en: {
        title: 'How to find a job in Finland',
        description:
          'Step-by-step guide: company career pages, Työmarkkinatori, and how JobCrawls keeps search and tracking in one workflow.',
        imageAlt: 'Job search planning, find a job in Finland guide',
      },
      fi: {
        title: 'Miten löydät töitä Suomessa',
        description:
          'Käytännön opas: yritysten urasivut, Työmarkkinatori ja miten JobCrawls yhdistää haun ja hakemusten seurannan.',
        imageAlt: 'Työnhaku Suomessa, opas',
      },
      sv: {
        title: 'Hur hittar man jobb i Finland',
        description:
          'Praktisk guide: företags karriärsidor, Työmarkkinatori och hur JobCrawls samlar sökning och ansökningsspårning.',
        imageAlt: 'Jobbsökning i Finland, guide',
      },
    },
    href: {
      en: getJobSearchGuideAbsolutePaths('en').pillar,
      fi: getJobSearchGuideAbsolutePaths('fi').pillar,
      sv: getJobSearchGuideAbsolutePaths('sv').pillar,
    },
    linkLabel: {
      en: 'Read the job search guide →',
      fi: 'Lue työnhakuopas →',
      sv: 'Läs jobbsökningsguiden →',
    },
  },
  {
    id: 'job-application-tracker',
    imageSrc: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=800&q=80&auto=format&fit=crop',
    imageLoading: 'lazy',
    copy: {
      en: {
        title: 'Job application tracker',
        description:
          'What to log per application, follow-up timing in Finland, duplicate applications across sources, and tying status to live listings.',
        imageAlt: 'Job application tracker guide',
      },
      fi: {
        title: 'Työhakemusten seuranta',
        description:
          'Mitä kirjata jokaista hakemusta varten, milloin otat yhteyttä Suomessa ja miten vältät tuplahakemukset eri lähteistä.',
        imageAlt: 'Työhakemusten seuranta, opas',
      },
      sv: {
        title: 'Jobbansökningsspårare',
        description:
          'Vad du loggar per ansökan, uppföljningsfönster i Finland och hur du undviker dubbletter mellan källor.',
        imageAlt: 'Jobbansökningsspårare, guide',
      },
    },
    href: {
      en: getJobSearchGuideAbsolutePaths('en').tracker,
      fi: getJobSearchGuideAbsolutePaths('fi').tracker,
      sv: getJobSearchGuideAbsolutePaths('sv').tracker,
    },
    linkLabel: {
      en: 'Read the tracker guide →',
      fi: 'Lue seurantaopas →',
      sv: 'Läs spårningsguiden →',
    },
  },
  {
    id: 'juniors-careers',
    imageSrc: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=80&auto=format&fit=crop',
    imageLoading: 'lazy',
    copy: {
      en: {
        title: 'Careers for juniors in Finland',
        description:
          'Entry-level roles, junior-friendly employers, and a career guide for new graduates starting in the Finnish job market.',
        imageAlt: 'Graduates starting careers in Finland',
      },
      fi: {
        title: 'Ura juniorille Suomessa',
        description:
          'Aloitustason roolit, junioriystävälliset työnantajat ja uravinkit uusille valmistuneille Suomen työmarkkinoilla.',
        imageAlt: 'Juniorit ja urapolku Suomessa',
      },
      sv: {
        title: 'Karriär för juniorer i Finland',
        description:
          'Ingångsroller, juniorvänliga arbetsgivare och karriärguide för nyexaminerade på den finska arbetsmarknaden.',
        imageAlt: 'Juniorer och karriärstart i Finland',
      },
    },
    href: {
      en: getJuniorsHubAbsolutePath('en'),
      fi: getJuniorsHubAbsolutePath('fi'),
      sv: getJuniorsHubAbsolutePath('sv'),
    },
    linkLabel: {
      en: 'Explore junior careers →',
      fi: 'Tutustu juniori-uraan →',
      sv: 'Utforska juniorkarriärer →',
    },
  },
  {
    id: 'summer-jobs',
    imageSrc: '/resources/summer-jobs.webp',
    copy: {
      en: {
        title: 'Summer Jobs in Finland',
        description: 'Short guide with a clear link to the job search filtered to the Summer job type.',
        imageAlt: 'People outdoors in summer, summer jobs resource on JobCrawls',
      },
      fi: {
        title: 'Kesätyöt Suomessa',
        description:
          'Lyhyt opas ja selkeä linkki työnhakuun, jossa työtyyppi on Kesätyö.',
        imageAlt: 'Kesä ja kesätyöt, JobCrawls',
      },
      sv: {
        title: 'Sommarjobb i Finland',
        description:
          'Kort guide med tydlig länk till jobbsökning filtrerad till jobbtypen Sommarjobb.',
        imageAlt: 'Sommar och sommarjobb, JobCrawls',
      },
    },
    href: {
      en: '/en/resources/summer-jobs',
      fi: '/fi/tyoelama/kesatyot',
      sv: '/sv/arbetsliv/sommarjobb-i-finland',
    },
    linkLabel: {
      en: 'Read guide & open search →',
      fi: 'Lue opas ja avaa haku →',
      sv: 'Läs guiden och öppna sökning →',
    },
  },
  {
    id: 'best-companies',
    imageSrc: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80&auto=format&fit=crop',
    imageLoading: 'lazy',
    copy: {
      en: {
        title: 'Best companies to work for in Finland',
        description:
          'Top employers ranked from live job listings for the Finnish market, including remote and hybrid, Helsinki and beyond. Pay transparency, flexibility, hiring signals.',
        imageAlt: 'Employer rankings',
      },
      fi: {
        title: 'Parhaat yritykset työpaikkana Suomessa',
        description:
          'Huipputyönantajat todellisista ilmoituksista Suomen työmarkkinalle (etätyö mukaan, Helsinki ja muut). Palkka‑avoimuus, joustavuus, rekrytointi.',
        imageAlt: 'Työnantajien sijoitukset',
      },
      sv: {
        title: 'Bästa företagen att jobba på i Finland',
        description:
          'Topp arbetsgivare från riktiga annonser mot Finland (distans och hybrid, Helsingfors m.m.). Lönetransparens, flexibilitet, rekrytering.',
        imageAlt: 'Arbetsgivarrankningar',
      },
    },
    href: {
      en: '/en/best-companies',
      fi: '/fi/parhaat-yritykset',
      sv: '/sv/basta-foretag',
    },
    linkLabel: {
      en: 'View rankings →',
      fi: 'Katso sijoitukset →',
      sv: 'Se rankningar →',
    },
  },
  {
    id: 'locations',
    imageSrc:
      'https://images.unsplash.com/photo-1597686512950-fd9e47e920b1?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8ZmlubGFuZCUyMG5hdHVyZXxlbnwwfHwwfHx8MA%3D%3D',
    copy: {
      en: {
        title: 'Explore Jobs by Location',
        description:
          'Discover job opportunities, salary insights, and market data across Finnish cities.',
        imageAlt: 'Explore Jobs by Location',
      },
      fi: {
        title: 'Tutustu työpaikkoihin sijainnin mukaan',
        description:
          'Löydä työpaikkoja, palkkatietoja ja markkinatietoja suomalaisten kaupunkien mukaan.',
        imageAlt: 'Työpaikat sijainnin mukaan',
      },
      sv: {
        title: 'Utforska jobb efter plats',
        description: 'Upptäck jobb, löneinsikter och marknadsdata i finska städer.',
        imageAlt: 'Jobb efter plats',
      },
    },
    href: {
      en: '/en/locations',
      fi: '/fi/sijainnit',
      sv: '/sv/platser',
    },
    linkLabel: {
      en: 'Explore Locations →',
      fi: 'Tutustu sijainteihin →',
      sv: 'Utforska platser →',
    },
  },
];

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function getSortedResourceHubArticleCards(): ResourceHubArticleCard[] {
  return [...RESOURCE_HUB_ARTICLE_CARDS].sort((a, b) => b.datePublished.localeCompare(a.datePublished));
}

export function getResourceHubArticleCards(): readonly ResourceHubArticleCard[] {
  return RESOURCE_HUB_ARTICLE_CARDS;
}

export function getResourceHubUtilityCards(): readonly ResourceHubUtilityCard[] {
  return RESOURCE_HUB_UTILITY_CARDS;
}

/** Runtime guard: every card must define all locales. */
export function assertResourceHubContentComplete(): void {
  const locales: ResourceHubLocale[] = ['en', 'fi', 'sv'];
  for (const card of RESOURCE_HUB_ARTICLE_CARDS) {
    if (!ISO_DAY.test(card.datePublished)) {
      throw new Error(`resource hub article "${card.id}" invalid datePublished`);
    }
    parseResourceIsoDayToUtcNoon(card.datePublished);
    for (const loc of locales) {
      if (!card.copy[loc] || !card.href[loc]) {
        throw new Error(`resource hub article "${card.id}" missing locale ${loc}`);
      }
    }
  }
  for (const card of RESOURCE_HUB_UTILITY_CARDS) {
    for (const loc of locales) {
      if (!card.copy[loc] || !card.href[loc]) {
        throw new Error(`resource hub utility "${card.id}" missing locale ${loc}`);
      }
    }
  }
}
